import {
  fetchBanners,
  fetchMarketById,
  fetchPage,
  fetchPageLinks,
  localeToMarketCode,
} from '@instabox/consumer-web-shared'
import { Stack } from '@instabox/consumer-web-ui'
import { useLoadScript } from '@react-google-maps/api'
import cn from 'classnames'
import { GetStaticProps, NextPage } from 'next'

import { fetchMarkets } from '../../actions/markets'
import { getAllPlaces, searchPlaces } from '../../actions/places'
import PlacesList from '../../components/PlacesList'
import PlacesMap from '../../components/PlacesMap'
import { ENV_CONFIG } from '../../constants/envConfig'
import PlacesSearchContainer from '../../containers/PlacesSearchContainer'
import { MapProvider } from '../../contexts/MapProvider'
import { MarketsProvider } from '../../contexts/MarketsProvider'
import { PlacesProvider } from '../../contexts/PlacesProvider'
import { Market } from '../../models/market'
import { Place, PlaceLocation } from '../../models/place'
import { LIBRARIES } from '../../utils/googleMaps'
import styles from './places.module.scss'

const SLUG = 'places'

interface PlacesPageProps {
  markets: Market[]
  currentMarket: Market
  places: Place[]
  locations: PlaceLocation[]
}

const PlacesPage: NextPage<PlacesPageProps> = ({ markets, currentMarket, places, locations }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: ENV_CONFIG.GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  })

  return (
    <MarketsProvider markets={markets} defaultMarket={currentMarket}>
      <PlacesProvider places={places}>
        <MapProvider
          defaultCenter={{
            lat: currentMarket.capital_region.latitude,
            lng: currentMarket.capital_region.longitude,
          }}>
          <div className={styles.container}>
            <div className={cn('row', styles.row)}>
              <div className="col-12 col-md-5 col-lg-4">
                <div className={styles.listCol}>
                  <Stack justify="space-between">
                    {isLoaded && <PlacesSearchContainer />}
                    <PlacesList />
                  </Stack>
                </div>
              </div>
              <div className={cn('col-12', 'col-md-7', 'col-lg-8', styles.placesCol)}>
                {isLoaded && <PlacesMap locations={locations} />}
              </div>
            </div>
          </div>
        </MapProvider>
      </PlacesProvider>
    </MarketsProvider>
  )
}

export const getStaticProps: GetStaticProps<PlacesPageProps> = async ({ locale }) => {
  const {
    data: { marketById: market },
  } = await fetchMarketById(localeToMarketCode(locale))
  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const {
    data: { markets },
  } = await fetchMarkets()
  const countryCode = locale.split('-')[1]?.toUpperCase()
  const currentMarket = markets.find(market => market.country_code === countryCode)
  const {
    data: { places },
  } = await searchPlaces({
    latitude: currentMarket.capital_region.latitude,
    longitude: currentMarket.capital_region.longitude,
    countryCode,
  })
  const {
    data: { places: locations },
  } = await getAllPlaces(locale as string, countryCode)

  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      market,
      pageLinks,
      fullBleed: true,
      markets,
      currentMarket,
      places,
      locations,
      banners,
      ...page,
    },
    revalidate: 60,
  }
}

export default PlacesPage
